import { Component, computed, effect, EventEmitter, inject, Injector, OnDestroy, OnInit, Output, signal, Signal, WritableSignal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatRadioChange } from '@angular/material/radio';
import { PermissionPipe } from '@app/features/admin/settings/permission/pipes/permission.pipe';
import { InventoryItem } from '@chartering/bunker-lube-fw/state/bunker-lube-fw.types';
import { TranslocoPipe, TranslocoService } from '@ngneat/transloco';
import { BodyComponent } from '@shared/components/body/body.component';
import { BoxComponent } from '@shared/components/box/box.component';
import { FooterComponent } from '@shared/components/footer/footer.component';
import { HeaderComponent } from '@shared/components/header/header.component';
import { SingleSelectionComponent } from '@shared/components/single-selection/single-selection.component';
import { RobState } from '@vessel-reports/rob/state/rob.state';
import { Detail } from '@vessel-reports/rob/state/rob.types';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'inventory-supplies',
  imports: [BodyComponent, BoxComponent, FooterComponent, HeaderComponent, SingleSelectionComponent, TranslocoPipe, ReactiveFormsModule, PermissionPipe],
  templateUrl: './inventory-supplies.component.html',
  styleUrl: './inventory-supplies.component.scss'
})
export class InventorySuppliesComponent implements OnInit, OnDestroy {
  protected readonly translocoService: TranslocoService = inject(TranslocoService);

  searchControl: UntypedFormControl = new UntypedFormControl(null);
  filteredInventoryItems: WritableSignal<InventoryItem[]> = signal([]);
  inventoryForm: FormGroup;
  selectedInventoryId: any;
  private readonly _state: RobState = inject(RobState);
  readonly inventories: Signal<any[]> = computed(() => this._state.inventories());
  readonly selectedDetail: Signal<Detail> = computed(() => this._state.selectedDetail());
  private readonly _formBuilder: FormBuilder = inject(FormBuilder);
  private readonly _injector: Injector = inject(Injector);
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  @Output() save: EventEmitter<any> = new EventEmitter<any>();
  @Output() back: EventEmitter<any> = new EventEmitter<any>();
  ngOnInit() {
    this.searchControl.valueChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe((searchTerm) => this.filterOptions(searchTerm));
    this.useEffect();
    this.inventoryForm = this._formBuilder.group({
      inventory_item_id: []
    });
  }

  useEffect() {
    effect(
      () => {
        if (this.selectedDetail()) {
          this.inventoryForm.patchValue(this.selectedDetail());
          this.selectedInventoryId = this.selectedDetail().inventory_item_id;
        }
        this.filteredInventoryItems.set(this.inventories());
      },
      { injector: this._injector }
    );
  }

  get getFormControl() {
    return this.inventoryForm.get('inventory_item_id') as FormControl;
  }

  ngOnDestroy() {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  selectInventoryItem(inventoryItem: MatRadioChange) {
    this.inventoryForm.get('inventory_item_id').setValue(inventoryItem.value);
  }

  onReset() {
    this.inventoryForm.reset();
  }

  onSave() {
    this.save.emit(this.inventoryForm.get('inventory_item_id').value);
  }

  filterOptions(searchTerm: string): void {
    if (!searchTerm) {
      this.filteredInventoryItems.set(this.inventories());
      return;
    }
    const lowerSearch = searchTerm.toLowerCase();
    this.filteredInventoryItems.set(this.inventories().filter((option) => option.name.toLowerCase().includes(lowerSearch)));
  }
  onBack() {
    this.back.emit();
  }
}
